import {
  Badge,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { inquiryApi } from '@texas/api/endpoints/inquiryApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  tierPriceId: number | null;
  currencyCode: string;
  title: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function InquiryTierPriceHistoryModal({
  tierPriceId,
  currencyCode,
  title,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useApiResource(
    inquiryApi.getTierPriceHistory,
  );

  useEffect(() => {
    if (isOpen && tierPriceId !== null) {
      refetch(tierPriceId);
    }
  }, [isOpen, tierPriceId, refetch]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {title
            ? t('inquiry.priceHistoryFor', { title })
            : t('inquiry.priceHistory')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {loading && <Spinner size="sm" />}
          {error && <ErrorDetails error={error} />}
          {data && (
            <Flex direction="column" gap={0}>
              {data.map((entry, idx) => (
                <Flex key={entry.id} gap={3} align="flex-start">
                  <Flex direction="column" align="center" flexShrink={0}>
                    <Box
                      w="10px"
                      h="10px"
                      borderRadius="full"
                      bg={idx === 0 ? 'blue.400' : 'gray.500'}
                      mt="4px"
                    />
                    {idx < data.length - 1 && (
                      <Box w="2px" flex="1" bg="gray.600" minH="24px" />
                    )}
                  </Flex>
                  <Box pb={4}>
                    <Flex align="center" gap={2} mb={0.5}>
                      <Text fontSize="sm" fontWeight="medium">
                        {entry.price != null
                          ? `${entry.price} ${currencyCode}`
                          : '—'}
                      </Text>
                      {idx === 0 && (
                        <Badge colorScheme="blue" fontSize="2xs">
                          {t('general.current')}
                        </Badge>
                      )}
                      {entry.acceptedDate && !entry.rejectedAt && (
                        <Badge colorScheme="green" fontSize="2xs">
                          {t('general.accepted')}
                        </Badge>
                      )}
                      {entry.rejectedAt && (
                        <Badge colorScheme="red" fontSize="2xs">
                          {t('inquiry.rejected')}
                        </Badge>
                      )}
                    </Flex>
                    {entry.acceptedDate && !entry.rejectedAt && (
                      <Text fontSize="xs" color="gray.400">
                        {entry.acceptedByUserName ?? 'Unknown'} -{' '}
                        {formatDate(Locale.En, entry.acceptedDate)}
                      </Text>
                    )}
                    {entry.rejectedAt && (
                      <Text fontSize="xs" color="gray.400">
                        {formatDate(Locale.En, entry.rejectedAt)}
                      </Text>
                    )}
                    {!entry.acceptedDate && !entry.rejectedAt && idx === 0 && (
                      <Text fontSize="xs" color="gray.500">
                        {t('inquiry.underReview')}
                      </Text>
                    )}
                  </Box>
                </Flex>
              ))}
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
